import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDiscoveryClient } from '../api/context';
import type { Folder, FolderCreateInput, FolderUpdateInput } from '../api/types';

export const foldersKey = ['discovery', 'folders'] as const;

export interface FolderListOptions {
  includeArchived?: boolean;
  search?: string;
}

export function useFolders(options?: FolderListOptions) {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: [...foldersKey, options ?? {}] as const,
    queryFn: () => client.folders.list(options),
  });
}

function useFolderInvalidator() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: foldersKey });
}

export function useCreateFolder() {
  const client = useDiscoveryClient();
  const invalidate = useFolderInvalidator();
  return useMutation({
    mutationFn: (input: FolderCreateInput) => client.folders.create(input),
    onSuccess: invalidate,
  });
}

export function useUpdateFolder() {
  const client = useDiscoveryClient();
  const invalidate = useFolderInvalidator();
  return useMutation({
    mutationFn: ({ publicId, patch }: { publicId: string; patch: FolderUpdateInput }) =>
      client.folders.update(publicId, patch),
    onSuccess: invalidate,
  });
}

/**
 * Apply one patch (archive, pin, unpin) to several folders, one request per
 * folder through the same endpoint the single-folder menu uses — there is no
 * bulk endpoint. Runs sequentially so a failure is reported per folder and
 * the server isn't hit with a burst; the folder list refetches once at the end.
 */
export function useBulkUpdateFolders() {
  const client = useDiscoveryClient();
  const invalidate = useFolderInvalidator();
  return useMutation({
    mutationFn: async ({
      publicIds,
      patch,
      onProgress,
    }: {
      publicIds: string[];
      patch: FolderUpdateInput;
      onProgress?: (done: number, total: number) => void;
    }) => {
      const failed: string[] = [];
      for (const [i, publicId] of publicIds.entries()) {
        try {
          await client.folders.update(publicId, patch);
        } catch {
          failed.push(publicId);
        }
        onProgress?.(i + 1, publicIds.length);
      }
      return { updated: publicIds.length - failed.length, failed };
    },
    onSettled: invalidate,
  });
}

/**
 * Deleting cascades every membership in the folder, so the server requires
 * `confirmLeadCount` to match. Archiving is the reversible alternative and is
 * what the sidebar offers first.
 */
export function useDeleteFolder() {
  const client = useDiscoveryClient();
  const invalidate = useFolderInvalidator();
  return useMutation({
    mutationFn: ({ publicId, confirmLeadCount }: { publicId: string; confirmLeadCount?: number }) =>
      client.folders.delete(publicId, confirmLeadCount),
    onSuccess: invalidate,
  });
}

export function useFolderGuidance(publicId: string | null) {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: [...foldersKey, publicId, 'guidance'] as const,
    queryFn: () => {
      if (!publicId) throw new Error('No folder selected');
      return client.folders.getGuidance(publicId);
    },
    enabled: !!publicId,
    retry: 1,
  });
}

export function useSaveFolderGuidance(publicId: string) {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      recommendedCampaigns: string | null;
      campaignIds?: string[];
      pitchNotes: string | null;
    }) =>
      client.folders.saveGuidance(publicId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...foldersKey, publicId, 'guidance'] }),
  });
}

export function selectFolderByPublicId(
  folders: Folder[] | undefined,
  publicId: string | null,
): Folder | null {
  if (!folders || !publicId) return null;
  return folders.find((f) => f.publicId === publicId) ?? null;
}

/**
 * Clipping campaigns an admin can attach to a folder.
 *
 * `available: false` means clipping hasn't shipped its catalogue endpoint yet
 * — the picker says so explicitly rather than rendering an empty list, which
 * would read as "there are no live campaigns".
 */
export function useClippingCampaigns() {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: ['clipping-campaigns'] as const,
    queryFn: () => client.clippingCampaigns.list(),
    staleTime: 5 * 60_000,
  });
}
